import { KeysTabsAnalysis } from "@app/types/samples.types";
import { PU_AM_CM_ANALYSIS_TYPES, SOURCE_APP } from "./calculationConstants";

/**
 * Busca un equipo por su código
 */
export const findEquipmentByCode = (
  equipmentList: any[],
  code?: string
): any | undefined => {
  if (!code) return undefined;
  
  return equipmentList.find(
    (equipment) => equipment?.codet?.toUpperCase() === code?.toUpperCase()
  );
};

/**
 * Busca un detector por su nombre
 */
export const findDetectorByName = (
  detectorsList: any[],
  name?: string
): any | undefined => {
  if (!name) return undefined;
  
  return detectorsList.find(
    (detector) => detector?.name?.toUpperCase() === name?.toUpperCase()
  );
};

/**
 * Busca equipos por código incluyendo geometría
 */
export const findEquipmentWithGeometry = (
  equipmentList: any[],
  detectorCode?: string
): number[] => {
  if (!detectorCode) return [];
  
  const parts = detectorCode.split("-");
  const equipment = equipmentList.find(
    (item) => item.codet === parts[1]
  )?.id;
  
  const geometry = equipmentList.find(
    (item) => item.codigo?.includes(parts[0])
  )?.id;
  
  return [equipment, geometry].filter((id): id is number => id !== undefined);
};

/**
 * Construye el payload de equipos según el tipo de análisis
 */
export const buildEquipmentPayload = (
  data: any,
  typeAnalysis: KeysTabsAnalysis,
  equipmentList: any[],
  detectorsList: any[],
  formGetValues?: (key: string) => any
) => {
  const isPuAmCm = PU_AM_CM_ANALYSIS_TYPES.includes(typeAnalysis);
  const isGenie = data.source_app_calculation_info?.toUpperCase() === SOURCE_APP.GENIE;
  
  let payload: any = {
    ...data,
    detector: undefined,
    first_counting_time: undefined,
    equipment: undefined,
    counting_date: undefined,
  };
  
  // Lógica específica para PU/AM/CM
  if (isPuAmCm) {
    const equipment = equipmentList.find(
      (item) => item.id === data.calculation_equipment_file
    )?.id;
    
    const findEquipment = typeof data.equipment !== "object"
      ? [equipment]
      : [];
    
    const findDetector = typeof data.detector !== "object"
      ? [detectorsList.find((item) => item.id === data.calculation_detectors)?.id]
      : [];
    
    payload = {
      ...payload,
      equipment: findEquipment,
      detector: findDetector,
    };
  } else {
    // Lógica para otros tipos de análisis
    if (isGenie) {
      const consolidateEquipment = findEquipmentWithGeometry(
        equipmentList,
        data.calculation_detector
      );
      
      payload = {
        ...payload,
        equipment: consolidateEquipment,
        detector: data.calculation_detectors ? [data.calculation_detectors] : undefined,
        first_counting_time: data.calculation_time_count,
      };
    } else {
      // Lógica para archivos EXE
      const codet_file = formGetValues?.("detector_file");
      const equip = codet_file
        ? equipmentList.find((item) => item.codet === codet_file)?.id
        : null;
      
      const currentGeometry = formGetValues?.("calculation_equipment")
        ?.find((item: any) => item.is_geometry)?.id;
      
      const arrEquip: (number | undefined)[] = [];
      if (equip !== null && equip !== undefined) arrEquip.push(equip);
      if (currentGeometry !== null && currentGeometry !== undefined) {
        arrEquip.push(currentGeometry);
      }
      
      payload = {
        ...payload,
        equipment: arrEquip,
        first_counting_time: formGetValues?.("first_counting_time"),
      };
    }
  }
  
  return payload;
};

/**
 * Encuentra el equipo por código o devuelve el objeto si ya es un objeto
 */
export const resolveEquipment = (
  equipment: any,
  equipmentList: any[]
): number[] => {
  if (typeof equipment !== "object") {
    const found = findEquipmentByCode(equipmentList, equipment);
    return found ? [found.id] : [];
  }
  return [equipment];
};

/**
 * Encuentra el detector por ID
 */
export const findDetectorById = (
  detectorsList: any[],
  detectorId?: number
): any | undefined => {
  if (!detectorId) return undefined;
  
  return detectorsList.find((item) => item.id === detectorId);
};
